import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getMyOrders, getMenu, updateProfile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Flame, Package, LogIn, ShoppingBag, RotateCcw, MapPin, Save } from 'lucide-react';

const STATUS_COLORS = { new: 'bg-orange-500', contacted: 'bg-blue-500', done: 'bg-green-600', cancelled: 'bg-red-600' };

export default function MyOrders() {
  const { user, loading, setUser } = useAuth();
  const { addItems, setOpen } = useCart();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [menuMap, setMenuMap] = useState({});
  const [busy, setBusy] = useState(true);
  const [profile, setProfile] = useState({ name: '', phone: '', address: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!user) { setBusy(false); return; }
    setProfile({ name: user.name || '', phone: user.phone || '', address: user.address || '' });
    Promise.all([getMyOrders(), getMenu()]).then(([o, m]) => {
      setOrders(o);
      const map = {}; m.forEach(x => { map[x.name] = x.image_url; });
      setMenuMap(map);
    }).finally(() => setBusy(false));
  }, [user]);

  const login = () => {
    localStorage.setItem('zs_post_login', '/account/orders');
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const reorder = (order) => {
    addItems(order.items.map(it => ({ name: it.name, price: it.price, qty: it.qty, image_url: menuMap[it.name] || '' })));
    setOpen(true);
    toast({ title: 'Added to cart', description: 'Your favourites are back in the cart — checkout when ready!' });
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const updated = await updateProfile(profile);
      setUser(updated);
      toast({ title: 'Details saved', description: 'Checkout will be faster next time.' });
    } catch {
      toast({ title: 'Could not save', variant: 'destructive' });
    } finally { setSavingProfile(false); }
  };

  return (
    <div className="pt-28 pb-20 min-h-screen">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em] mb-4">
          <Package className="w-3.5 h-3.5" /> My Orders
        </div>
        <h1 className="font-display text-4xl md:text-6xl">Your <span className="text-orange-500 fire-glow">Order History</span></h1>

        {!loading && !user && (
          <div className="mt-12 text-center bg-neutral-900 border border-white/10 rounded-3xl p-12">
            <ShoppingBag className="w-12 h-12 text-orange-500 mx-auto" />
            <p className="mt-4 text-white/70">Sign in to see your past orders and reorder your favourites.</p>
            <Button data-testid="orders-login-btn" onClick={login} className="mt-6 h-12 px-7 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><LogIn className="w-4 h-4 mr-2" /> Sign in with Google</Button>
          </div>
        )}

        {user && (
          <div className="mt-8 food-card bg-neutral-900 border border-white/10 rounded-2xl p-6 will-change-transform" data-testid="delivery-details">
            <div className="flex items-center gap-2 text-orange-400 text-xs uppercase tracking-widest font-semibold mb-4"><MapPin className="w-4 h-4" /> Delivery Details (saved for faster checkout)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="profile-name" placeholder="Full name" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="h-11 bg-black/50 border-white/10" />
              <Input data-testid="profile-phone" placeholder="Phone / WhatsApp" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} className="h-11 bg-black/50 border-white/10" />
            </div>
            <Textarea data-testid="profile-address" placeholder="Delivery address" value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} className="mt-3 bg-black/50 border-white/10" />
            <Button data-testid="save-profile" onClick={saveProfile} disabled={savingProfile} className="mt-4 h-11 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Save className="w-4 h-4 mr-2" /> {savingProfile ? 'Saving…' : 'Save Details'}</Button>
          </div>
        )}

        {user && !busy && orders.length === 0 && (
          <div className="mt-8 text-center bg-neutral-900 border border-white/10 rounded-3xl p-12">
            <Flame className="w-12 h-12 text-orange-500 mx-auto" />
            <p className="mt-4 text-white/70">No orders yet. Time for some crazy food!</p>
            <Link to="/menu"><Button className="mt-6 h-12 px-7 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Browse Menu</Button></Link>
          </div>
        )}

        {user && orders.length > 0 && (
          <div className="mt-8 space-y-4">
            {orders.map(o => (
              <div key={o.id} data-testid={`my-order-${o.id}`} className="food-card bg-neutral-900 border border-white/10 rounded-2xl p-5 will-change-transform">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl text-orange-500">₹{o.total}</span>
                    <Badge variant="outline" className="border-white/20 uppercase text-[10px]">{o.platform}</Badge>
                    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/70">
                      <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[o.status] || 'bg-orange-500'}`} /> {o.status}
                    </span>
                  </div>
                  <span className="text-xs text-white/40">{new Date(o.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {o.items.map((it, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full bg-black/40 border border-white/10 text-sm text-white/80">{it.name} <span className="text-orange-400">×{it.qty}</span></span>
                  ))}
                </div>
                <Button data-testid={`reorder-${o.id}`} onClick={() => reorder(o)} variant="outline" className="mt-4 h-10 rounded-full border-orange-500/50 text-orange-400 hover:bg-orange-500 hover:text-white bg-transparent font-bold uppercase tracking-widest text-xs"><RotateCcw className="w-4 h-4 mr-2" /> Reorder</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
